import { Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { Card, Badge, Skeleton } from '@bbpos/ui';
import { formatPaise } from '@/utils/currency';
import { useTenantCurrency } from '@/hooks/use-tenant-currency';
import { dashboardService } from '@/services/dashboard.service';
import { useDashboardStore } from '@/stores/use-dashboard-store';
import { formatBillNumber } from '@/features/bills/bill-number';

const STATUS_VARIANT: Record<string, 'success' | 'warning' | 'danger' | 'info'> = {
  COMPLETED: 'success',
  DRAFT: 'info',
  PARKED: 'warning',
  VOIDED: 'danger',
};

function formatTime(iso: string): string {
  return new Date(iso).toLocaleTimeString('en-IN', {
    hour: '2-digit',
    minute: '2-digit',
    hour12: true,
  });
}

export function RecentBillsWidget() {
  const currency = useTenantCurrency();
  const locationId = useDashboardStore((s) => s.selectedLocationId);

  const { data, isLoading } = useQuery({
    queryKey: ['dashboard', 'recent-bills', locationId],
    queryFn: () => dashboardService.getRecentBills(locationId, 5),
    staleTime: 30_000,
  });

  if (isLoading) {
    return (
      <Card className="p-4 lg:p-6 shadow-md backdrop-blur-sm bg-white/80 dark:bg-neutral-900/80">
        <Skeleton variant="text" width="40%" height={16} />
        <div className="mt-4 space-y-3">
          {Array.from({ length: 5 }).map((_, i) => (
            <Skeleton key={i} variant="rect" height={40} />
          ))}
        </div>
      </Card>
    );
  }

  const bills = data?.data ?? [];

  return (
    <Card className="p-4 lg:p-6 shadow-md backdrop-blur-sm bg-white/80 dark:bg-neutral-900/80 transition-shadow duration-200 hover:shadow-lg animate-slide-up">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-indigo-500 to-violet-500 flex items-center justify-center text-white">
            <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2} aria-hidden="true">
              <path strokeLinecap="round" strokeLinejoin="round" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
            </svg>
          </div>
          <h3 className="text-sm font-semibold text-neutral-700 dark:text-neutral-200">
            Recent Bills
          </h3>
        </div>
        <Link
          to="/bills"
          className="text-xs font-medium text-brand-600 dark:text-brand-400 hover:underline"
        >
          View All &rarr;
        </Link>
      </div>

      {bills.length === 0 ? (
        <p className="text-xs text-neutral-400 py-6 text-center">
          No bills yet. Start billing to see recent activity.
        </p>
      ) : (
        <div className="space-y-2">
          {bills.map((bill) => (
            <Link
              key={bill.id}
              to={`/bills/${bill.id}`}
              className="flex items-center justify-between rounded-lg px-3 py-2.5 hover:bg-neutral-50 dark:hover:bg-neutral-800 transition-colors"
            >
              <div className="min-w-0 flex-1">
                <p className="text-sm font-medium text-neutral-900 dark:text-neutral-100 tabular-nums truncate">
                  {formatBillNumber(bill)}
                </p>
                <p className="text-xs text-neutral-500 dark:text-neutral-400">
                  {bill.customer_name ?? bill.payment_method} &middot; {formatTime(bill.created_at)}
                </p>
              </div>
              <div className="flex items-center gap-2 ml-3 shrink-0">
                <span className="text-sm font-semibold tabular-nums text-neutral-900 dark:text-neutral-100">
                  {formatPaise(bill.grand_total, currency)}
                </span>
                <Badge variant={STATUS_VARIANT[bill.status] ?? 'info'} >
                  {bill.status}
                </Badge>
              </div>
            </Link>
          ))}
        </div>
      )}
    </Card>
  );
}
